import { useQuery } from 'data-fetching-client';
import { GET_FEATURES_BY_API_NAMES, GET_FEATURES_WITH_DEFAULTS } from 'pricing-pages-api/features/queries';
import { FEATURES_DEFAULTS } from 'pricing-pages-lib/constants/EarlyRequesterNames';
import { pinKeyFeatures } from 'pricing-pages-lib/utils/featureUtils';
import { getI18nLangLocale } from 'pricing-pages-lib/utils/languageUtils';
import { CMS, MARKETING, OPERATIONS, SALES, SERVICE } from 'self-service-api/constants/ProductLines';
import { getHighlightsForProductLine } from 'ui-addon-upgrades/_core/features/FeaturesUtils';
import { JunePinnedFeaturesConfig, PinnedFeaturesConfig } from '../components/feature-table/PinnedFeaturesConfig';
import { useAppSelector } from '../reducers';
import { isUngatedForHubSpotCreditsSelector } from '../selectors/GatingSelectors';
export const useFeaturesForApiNames = highlightedProducts => {
  const i18nLangLocale = getI18nLangLocale();
  const {
    data,
    loading,
    error
  } = useQuery(GET_FEATURES_BY_API_NAMES, {
    variables: {
      productApiNames: highlightedProducts,
      i18nLangLocale
    },
    skip: !(highlightedProducts !== null && highlightedProducts !== void 0 && highlightedProducts.length)
  });
  return {
    features: (data === null || data === void 0 ? void 0 : data.features) || [],
    loading,
    error
  };
};

/**
 * This hook is used to get the features for a given product line(s)
 *
 * @param productLines the product lines will be converted to a ProductLine <> ApiName map using getHighlightsForProductLine
 * @param productLineToApiName a map of product lines to associated api names
 *
 * We will always end up with a ProductLine <> ApiName map.
 * - The ProductLine is used to determine which query to send/skip
 * - The ApiName is what's associated with the product line and is sent as part of the feature store query
 *
 * @see {@link https://sourcegraph.hubteam.com/git.hubteam.com/HubSpot/ui-addon-upgrades@5cbb1dc1c8d91dea48b97b067cf984eb2e072a96/-/blob/ui-addon-upgrades/static/js/_core/features/FeaturesUtils.ts?L12 | getHighlightsForProductLine}
 */
export const useFeaturesByProductLines = ({
  productLines,
  productLineToApiName
}) => {
  productLineToApiName = productLines ? productLines.reduce((acc, productLine) => {
    acc[productLine] = getHighlightsForProductLine(productLine);
    return acc;
  }, {}) : productLineToApiName;

  // Query for Marketing
  const marketingData = useFeaturesForApiNames(productLineToApiName[MARKETING]);

  // Query for Sales
  const salesData = useFeaturesForApiNames(productLineToApiName[SALES]);

  // Query for Service
  const serviceData = useFeaturesForApiNames(productLineToApiName[SERVICE]);

  // Query for CMS
  const cmsData = useFeaturesForApiNames(productLineToApiName[CMS]);

  // Query for Operations
  const operationsData = useFeaturesForApiNames(productLineToApiName[OPERATIONS]);
  const featuresByProductLine = Object.keys(productLineToApiName).reduce((acc, productLine) => {
    switch (productLine) {
      case MARKETING:
        acc[productLine] = marketingData.features;
        break;
      case SALES:
        acc[productLine] = salesData.features;
        break;
      case SERVICE:
        acc[productLine] = serviceData.features;
        break;
      case CMS:
        acc[productLine] = cmsData.features;
        break;
      case OPERATIONS:
        acc[productLine] = operationsData.features;
        break;
      default:
    }
    return acc;
  }, {});
  const loading = marketingData.loading || salesData.loading || serviceData.loading || cmsData.loading || operationsData.loading;
  const error = [marketingData.error, salesData.error, serviceData.error, cmsData.error, operationsData.error].filter(Boolean);
  return {
    featuresByProductLine,
    loading,
    error: error.length ? error : undefined
  };
};
export const useFeaturesWithDefaults = () => {
  const i18nLangLocale = getI18nLangLocale();
  const {
    data,
    loading,
    error
  } = useQuery(GET_FEATURES_WITH_DEFAULTS, {
    variables: {
      i18nLangLocale,
      earlyRequestName: FEATURES_DEFAULTS
    }
  });
  return {
    features: (data === null || data === void 0 ? void 0 : data.featuresWithDefaults) || {},
    loading,
    error
  };
};
export const usePinnedFeaturesByProductLine = productLineToApiName => {
  const {
    featuresByProductLine,
    loading,
    error
  } = useFeaturesByProductLines({
    productLineToApiName
  });
  const isUngatedForHubSpotCredits = useAppSelector(isUngatedForHubSpotCreditsSelector);
  const pinnedConfig = isUngatedForHubSpotCredits ? JunePinnedFeaturesConfig : PinnedFeaturesConfig;
  const pinnedFeaturesByProductLine = Object.keys(featuresByProductLine).reduce((acc, productLine) => {
    var _featuresByProductLin;
    acc[productLine] = pinKeyFeatures((_featuresByProductLin = featuresByProductLine[productLine]) !== null && _featuresByProductLin !== void 0 ? _featuresByProductLin : [], productLine, pinnedConfig);
    return acc;
  }, {});
  return {
    featuresByProductLine: pinnedFeaturesByProductLine,
    loading,
    error
  };
};